import React, { useState, useEffect } from 'react';
import { OnboardingStep } from '../../../contexts/OnboardingContext';
import { useOnboarding } from '../../../contexts/OnboardingContext';
import { 
  LinkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowRightIcon,
  LockClosedIcon,
  GlobeAltIcon,
  CogIcon
} from '@heroicons/react/24/outline';

interface ConnectionTutorialStepProps {
  step: OnboardingStep;
}

interface ConnectionState {
  step: number;
  status: 'idle' | 'connecting' | 'authorizing' | 'success' | 'error';
  error?: string;
  appName?: string;
}

export function ConnectionTutorialStep({ step }: ConnectionTutorialStepProps) {
  const { completeStep } = useOnboarding();
  const [connectionState, setConnectionState] = useState<ConnectionState>({
    step: 1,
    status: 'idle'
  });
  const [selectedApp, setSelectedApp] = useState<string>('');

  // Demo apps for tutorial
  const tutorialApps = [
    { id: 'gmail', name: 'Gmail', logo: '📧', description: 'Email management', difficulty: 'Easy' },
    { id: 'slack', name: 'Slack', logo: '💬', description: 'Team communication', difficulty: 'Easy' },
    { id: 'github', name: 'GitHub', logo: '🐙', description: 'Code repository', difficulty: 'Medium' }
  ];

  const connectionSteps = [
    {
      title: 'Select an App',
      description: 'Choose which service you want to connect',
      icon: LinkIcon,
      status: connectionState.step >= 1 ? 'completed' : 'pending'
    },
    {
      title: 'OAuth Authorization',
      description: 'Sign in to authorize access to your account',
      icon: LockClosedIcon,
      status: connectionState.step >= 2 ? 'completed' : connectionState.step === 1 ? 'active' : 'pending'
    },
    {
      title: 'Configure Access',
      description: 'Set permissions and connection settings',
      icon: CogIcon,
      status: connectionState.step >= 3 ? 'completed' : connectionState.step === 2 ? 'active' : 'pending'
    },
    {
      title: 'Test Connection',
      description: 'Verify the connection is working properly',
      icon: CheckCircleIcon,
      status: connectionState.step >= 4 ? 'completed' : connectionState.step === 3 ? 'active' : 'pending'
    }
  ];

  const simulateConnection = async (appName: string) => {
    setConnectionState({ step: 1, status: 'connecting', appName });

    // Step 1: App selection (already done)
    await new Promise(resolve => setTimeout(resolve, 1000));
    setConnectionState({ step: 2, status: 'authorizing', appName });

    // Step 2: OAuth authorization
    await new Promise(resolve => setTimeout(resolve, 2000));
    setConnectionState({ step: 3, status: 'connecting', appName });

    // Step 3: Configure access
    await new Promise(resolve => setTimeout(resolve, 1500));
    setConnectionState({ step: 4, status: 'connecting', appName });

    // Step 4: Test connection
    await new Promise(resolve => setTimeout(resolve, 1000));
    setConnectionState({ step: 4, status: 'success', appName });

    // Auto-complete after success
    setTimeout(() => {
      completeStep(step.id);
    }, 2000);
  };

  const handleAppSelect = (appId: string) => {
    const app = tutorialApps.find(a => a.id === appId);
    if (app) {
      setSelectedApp(appId);
      simulateConnection(app.name);
    }
  };

  const resetDemo = () => {
    setConnectionState({ step: 1, status: 'idle' });
    setSelectedApp('');
  };

  return (
    <div>
      <div className="text-center mb-6">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
          <LinkIcon className="h-8 w-8 text-green-600 dark:text-green-400" />
        </div>
        <h3 className="mt-4 text-xl font-bold text-gray-900 dark:text-white">
          {step.title}
        </h3>
        <p className="mt-2 text-gray-600 dark:text-gray-300">
          {step.description}
        </p>
      </div>

      {/* Connection Process Overview */}
      <div className="mb-8">
        <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          How App Connections Work
        </h4>
        
        <div className="space-y-4">
          {connectionSteps.map((stepItem, index) => (
            <div
              key={index}
              className={`flex items-start p-4 rounded-lg border-2 transition-all ${
                stepItem.status === 'completed' 
                  ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
                  : stepItem.status === 'active'
                  ? 'border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20'
                  : 'border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700'
              }`}
            >
              <div className={`flex-shrink-0 flex h-10 w-10 items-center justify-center rounded-full ${
                stepItem.status === 'completed'
                  ? 'bg-green-100 dark:bg-green-800'
                  : stepItem.status === 'active'
                  ? 'bg-indigo-100 dark:bg-indigo-800'
                  : 'bg-gray-200 dark:bg-gray-600'
              }`}>
                {stepItem.status === 'completed' ? (
                  <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                ) : (
                  <stepItem.icon className={`h-6 w-6 ${
                    stepItem.status === 'active' 
                      ? 'text-indigo-600 dark:text-indigo-400' 
                      : 'text-gray-400'
                  }`} />
                )}
              </div>
              
              <div className="ml-4 flex-1">
                <h5 className={`font-medium ${
                  stepItem.status === 'completed'
                    ? 'text-green-900 dark:text-green-100'
                    : stepItem.status === 'active'
                    ? 'text-indigo-900 dark:text-indigo-100'
                    : 'text-gray-700 dark:text-gray-300'
                }`}>
                  {stepItem.title}
                </h5>
                <p className={`text-sm mt-1 ${
                  stepItem.status === 'completed'
                    ? 'text-green-700 dark:text-green-300'
                    : stepItem.status === 'active'
                    ? 'text-indigo-700 dark:text-indigo-300'
                    : 'text-gray-500 dark:text-gray-400'
                }`}>
                  {stepItem.description}
                </p>
              </div>

              {stepItem.status === 'active' && connectionState.status === 'connecting' && (
                <div className="flex-shrink-0">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Interactive Demo */}
      <div className="mb-6">
        <h4 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          🎯 Try It Yourself
        </h4>

        {connectionState.status === 'idle' ? (
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Select an app below to see the connection process in action:
            </p>
            
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {tutorialApps.map((app) => (
                <button
                  key={app.id}
                  onClick={() => handleAppSelect(app.id)}
                  className="p-4 text-left border border-gray-200 dark:border-gray-600 rounded-lg hover:border-indigo-300 dark:hover:border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-all bg-white dark:bg-gray-800"
                >
                  <div className="flex items-center mb-2">
                    <span className="text-2xl mr-3">{app.logo}</span>
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">{app.name}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{app.description}</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      app.difficulty === 'Easy' 
                        ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300'
                        : 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300'
                    }`}>
                      {app.difficulty}
                    </span>
                    <ArrowRightIcon className="h-4 w-4 text-gray-400" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-6 bg-white dark:bg-gray-800">
            <div className="flex items-center justify-between mb-4">
              <h5 className="font-medium text-gray-900 dark:text-white">
                Connecting to {connectionState.appName}
              </h5>
              {connectionState.status === 'success' && (
                <CheckCircleIcon className="h-6 w-6 text-green-500" />
              )}
            </div>

            {/* OAuth Simulation */}
            {connectionState.step >= 2 && connectionState.status !== 'success' && (
              <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="flex items-center mb-2">
                  <GlobeAltIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" />
                  <span className="font-medium text-blue-900 dark:text-blue-100">OAuth Authorization</span>
                </div>
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  In a real scenario, you'd be redirected to {connectionState.appName}'s login page to authorize access.
                </p>
                {connectionState.status === 'authorizing' && (
                  <div className="mt-2 flex items-center text-sm text-blue-700 dark:text-blue-300">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
                    Waiting for authorization...
                  </div>
                )}
              </div>
            )}

            {/* Success State */}
            {connectionState.status === 'success' && (
              <div className="space-y-4">
                <div className="flex items-center justify-center text-green-600 dark:text-green-400 mb-4">
                  <CheckCircleIcon className="h-8 w-8 mr-2" />
                  <span className="text-lg font-medium">Connection Successful!</span>
                </div>
                
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <h6 className="font-medium text-green-900 dark:text-green-100 mb-2">
                    What happens next:
                  </h6>
                  <ul className="text-sm text-green-800 dark:text-green-200 space-y-1">
                    <li>• Your {connectionState.appName} connection is now active</li>
                    <li>• API credentials are securely stored</li>
                    <li>• You can now use this connection in your MCP configurations</li>
                    <li>• The connection will be automatically refreshed as needed</li>
                  </ul>
                </div>

                <button
                  onClick={resetDemo}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Try Another App
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Security Info */}
      <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-4 mb-6">
        <h4 className="text-sm font-medium text-purple-900 dark:text-purple-100 mb-2">
          🔐 Security & Privacy
        </h4>
        <ul className="text-sm text-purple-800 dark:text-purple-200 space-y-1">
          <li>• All connections use OAuth 2.0 for secure authorization</li>
          <li>• Your credentials are encrypted and stored securely</li>
          <li>• You can revoke access at any time</li>
          <li>• We only request the minimum required permissions</li>
        </ul>
      </div>

      {/* Tips */}
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-6">
        <h4 className="text-sm font-medium text-yellow-900 dark:text-yellow-100 mb-2">
          💡 Pro Tips
        </h4>
        <ul className="text-sm text-yellow-800 dark:text-yellow-200 space-y-1">
          <li>• Test your connections after setup to ensure they work correctly</li>
          <li>• Some apps may require additional configuration or API keys</li>
          <li>• Check connection status regularly in your dashboard</li>
          <li>• Disconnect unused connections to maintain security</li>
        </ul>
      </div>

      {/* Continue Button */}
      <div className="flex justify-end">
        <button
          onClick={() => completeStep(step.id)}
          disabled={connectionState.status !== 'success' && connectionState.status !== 'idle'}
          className={`px-4 py-2 rounded-md font-medium ${
            connectionState.status === 'success' || connectionState.status === 'idle'
              ? 'bg-indigo-600 text-white hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500'
              : 'bg-gray-300 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
          }`}
        >
          {connectionState.status === 'success' ? 'Great! Continue' : 'Continue'}
        </button>
      </div>
    </div>
  );
}